'use client';

// Partie « À toi » dans la VIDÉO exportée (lot 7) : pas d'élève pour
// répondre, la question est jouée en démonstration selon `exportBehavior` :
//   - `auto-reveal` : la question et ses choix, un compte à rebours, puis la
//     bonne réponse et son explication ;
//   - `cta` : la question (ou l'outil) et « À toi sur JeRaisonne ! ».
// Mêmes couleurs et mêmes tailles que le panneau de l'app ; rien
// d'interactif. Placé par le plan de la vidéo (`panelBottom` : au-dessus de
// l'interface des Shorts quand la figure le permet).

import { Check, Wrench } from 'lucide-react';
import { JRAF_PALETTE, type JrafStep, type VideoGatePlan } from '@jeraisonne/animation';
import { cn } from '../../lib/utils';
import { AnimationMathText } from './animation-math';
import { SCENE_TARGET, SCENE_TEXT, SCENE_TEXT_LARGE, SCENE_TEXT_SMALL } from './scene-sizes';

const C = JRAF_PALETTE;

export const VIDEO_CTA_QUESTION = 'À toi de répondre sur JeRaisonne !';
export const VIDEO_CTA_TOOL = 'Essaie cet outil sur JeRaisonne !';

export function VideoGatePanel({
  step,
  gate,
  revealed,
  progress,
  maxHeight,
}: {
  step: JrafStep;
  gate: VideoGatePlan;
  revealed: boolean;
  /** 0 → 1 jusqu'à la réponse (`auto-reveal`) ou jusqu'à la fin (`cta`) */
  progress: number;
  /** hauteur maximale du panneau (px du canevas : la moitié de la scène) */
  maxHeight: number;
}) {
  if (step.action !== 'question.ask' && step.action !== 'tool.open') return null;
  const countdown =
    gate.mode === 'auto-reveal' && !revealed && gate.revealMs !== null
      ? Math.max(1, Math.ceil(((1 - progress) * (gate.revealMs - gate.openMs)) / 1000))
      : null;
  const cta =
    gate.mode === 'cta'
      ? step.action === 'tool.open'
        ? VIDEO_CTA_TOOL
        : VIDEO_CTA_QUESTION
      : null;
  return (
    // le bandeau « À toi… » est posé SUR le bord du panneau : le panneau garde
    // la hauteur vérifiée par la mise en page (moitié de la scène au plus)
    <div className="absolute inset-x-3" style={{ bottom: gate.panelBottom }}>
      <section
        data-video-gate={gate.stepId}
        aria-label={step.action === 'question.ask' ? 'Question' : 'Outil'}
        className={cn(
          'relative flex flex-col gap-3 overflow-hidden rounded-2xl p-4 shadow-2xl',
          // le bandeau « À toi… » chevauche le haut du panneau : le texte passe dessous
          cta && 'pt-7',
        )}
        style={{ maxHeight, backgroundColor: C.paper, color: C.ink }}
      >
        {countdown !== null && (
          <>
            {/* temps avant la réponse : barre en haut, chiffre à la place du bouton de l'app */}
            <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: '#e6e9ef' }}>
              <div
                className="h-full"
                style={{ width: `${Math.round(progress * 1000) / 10}%`, backgroundColor: C.gold }}
              />
            </div>
            <span
              data-video-countdown={countdown}
              className="absolute right-2 top-3 flex h-9 w-9 items-center justify-center rounded-full text-[17px] font-bold tabular-nums"
              style={{ backgroundColor: C.primary, color: C.paper }}
            >
              {countdown}
            </span>
          </>
        )}
        {step.action === 'tool.open' ? (
          <p
            className={cn(
              'flex items-start gap-2 pr-10 font-semibold leading-snug',
              SCENE_TEXT_LARGE,
            )}
          >
            <Wrench size={20} className="mt-1 shrink-0" color={C.primary} aria-hidden="true" />
            <span>
              <AnimationMathText>{step.prompt}</AnimationMathText>
            </span>
          </p>
        ) : (
          <>
            <p className={cn('pr-10 font-semibold leading-snug', SCENE_TEXT_LARGE)}>
              <AnimationMathText>{step.prompt}</AnimationMathText>
            </p>
            {revealed ? (
              <>
                <div
                  data-video-answer=""
                  className={cn(
                    'flex w-full items-center gap-2 rounded-xl border-2 px-3 py-2',
                    SCENE_TARGET,
                    SCENE_TEXT,
                  )}
                  style={{ borderColor: C.green, backgroundColor: '#e8f8ef' }}
                >
                  <span className="flex-1">
                    <AnimationMathText>{step.choices[step.correct] ?? ''}</AnimationMathText>
                  </span>
                  <Check aria-label="bonne réponse" size={20} color={C.green} />
                </div>
                <p className={cn('leading-snug', SCENE_TEXT_SMALL)}>
                  <AnimationMathText>{step.explanation}</AnimationMathText>
                </p>
              </>
            ) : (
              // pas de doigt dans une vidéo : des choix plus serrés que les boutons de
              // l'app, pour que quatre choix tiennent dans la moitié de l'écran
              <ul className="flex flex-col gap-1.5">
                {step.choices.map((choice, i) => (
                  <li
                    key={i}
                    data-video-choice={i}
                    className={cn(
                      'flex min-h-[40px] w-full items-center gap-2 rounded-xl border-2 px-3 py-1.5',
                      SCENE_TEXT,
                    )}
                    style={{ borderColor: '#d9dde5', backgroundColor: C.paper }}
                  >
                    <AnimationMathText>{choice}</AnimationMathText>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
      {cta && (
        <p
          data-video-cta=""
          className={cn(
            'absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-2 font-bold shadow-lg',
            SCENE_TEXT,
          )}
          style={{ backgroundColor: C.gold, color: C.night }}
        >
          {cta}
        </p>
      )}
    </div>
  );
}
